// Framework dependencies
import React, { FC, useEffect, useState } from 'react';

// Packages dependencies
import { Box, Text } from '@updater/ui-uds';

// Local dependencies
import { makeForwardMailReceiptsForVerticals } from 'flows/core/utils/card-makers';
import { ItemKinds } from 'types';
import { BasicReceiptCard } from 'flows/core/components';

import { cardsPriority, sortByStatus } from 'utils/cards';
import { ReceiptPayload } from 'flows/core/types/cards';

import { FORWARD_MAIL_RECEIPT_PATH } from '../constants';
import {
  DataForCards,
  TargetConfigType,
} from 'flows/core/hooks/use-data-for-cards';
import { useForwardMailUserDataStore } from '../store';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';
import { DOMAIN, OBJECT, PAGE, VERB } from '../constants/analytics-tracker';
import { formatFormDataToStore } from '../utils';
import { SubmittedFormProps } from '../types';
import { Disclaimer } from './Disclaimer';

interface SubmittedFormItemsProps {
  dataForCards?: DataForCards;
  submittedForms: SubmittedFormProps[];
  onItemClickTrackPage: PAGE;
}

const cardMakerFunctions = {
  forward_mail: makeForwardMailReceiptsForVerticals,
};

export const SubmittedFormItems: FC<SubmittedFormItemsProps> = ({
  dataForCards,
  submittedForms,
  onItemClickTrackPage,
}) => {
  const [submitted, setSubmitted] = useState<ReceiptPayload[]>([]);
  const { resetUserData, setUserData } = useForwardMailUserDataStore();
  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: DOMAIN.FORWARD_MAIL,
  });
  /**
   * @method
   * @description Get the card target configuration object.
   * @returns {TargetConfigType}
   */
  const cardTargetConfig = (doneItems: [{ id: string }]) => {
    return doneItems.reduce((acc: Record<string, unknown>, actual) => {
      const { id } = actual;
      acc[id] = `${FORWARD_MAIL_RECEIPT_PATH}${actual.id}`;
      return acc;
    }, {});
  };

  const currentFormById = (id: string) => {
    return submittedForms.reduce(
      (acc: Record<string, unknown>, actual: { id: string }) => {
        let accumulator = acc;
        if (actual.id === id) {
          accumulator = actual;
        }
        return accumulator;
      },
      {}
    );
  };

  const onClickCard = (submittedCardData: ReceiptPayload['data']) => {
    const submittedCardId =
      'id' in submittedCardData ? submittedCardData.id : '';
    trackEvent({
      verb: VERB.CLICKED,
      object: OBJECT.EDIT_FORM,
      pageName: onItemClickTrackPage,
      details: {
        formId: submittedCardId,
      },
    });

    if (submittedForms.length) {
      const currentForm = currentFormById(submittedCardId as string);

      resetUserData();
      setUserData({
        ...formatFormDataToStore({
          ...submittedCardData,
          ...currentForm,
        } as unknown as SubmittedFormProps),
      });
    }
  };

  useEffect(() => {
    if (dataForCards) {
      const filteredItems = dataForCards.items.filter(
        (item: DataForCards['items'][0]) => item.type === ItemKinds.FORWARD_MAIL
      );

      const doneItems = filteredItems.filter(
        (item: (typeof filteredItems)[0]) => item.status !== 'cancelled'
      );

      const cardMakerFunction = cardMakerFunctions[ItemKinds.FORWARD_MAIL];

      if (doneItems?.length) {
        const doneCards = cardMakerFunction({
          items: doneItems,
          // @ts-expect-error FIXME: TS2345 - strict mode migration
          target: cardTargetConfig(doneItems) as TargetConfigType,
        });

        setSubmitted(sortByStatus(doneCards, cardsPriority));
      }
    }
  }, [dataForCards]);

  return (
    <>
      {submitted.length > 0 && (
        <>
          <Text as="h3" variant="l" mb="s" mt="xs">
            Submitted Forms
          </Text>
          <Disclaimer />
          {submitted.map((submittedCard: ReceiptPayload) => (
            <Box
              mb="s"
              key={'id' in submittedCard.data ? submittedCard.data.id : ''}
              onClick={() => onClickCard(submittedCard.data)}
            >
              <BasicReceiptCard {...submittedCard} showSubtitle />
            </Box>
          ))}
        </>
      )}
    </>
  );
};
